if (!customElements.get('media-gallery')) {
  customElements.define(
    'media-gallery',
    class MediaGallery extends HTMLElement {
      constructor() {
        super();
        this.elements = {
          liveRegion: this.querySelector('[id^="GalleryStatus"]'),
          viewer: this.querySelector('[id^="GalleryViewer"]'),
          thumbnails: this.querySelector('[id^="GalleryThumbnails"]'),
        };
        this.mql = window.matchMedia('(min-width: 750px)');
        if (!this.elements.thumbnails) return;

        this.elements.viewer.addEventListener('slideChanged', debounce(this.onSlideChanged.bind(this), 500));
        this.elements.thumbnails.querySelectorAll('[data-target]').forEach((mediaToSwitch) => {
          mediaToSwitch
            .querySelector('button')
            .addEventListener('click', this.setActiveMedia.bind(this, mediaToSwitch.dataset.target, false));
        });
        if (this.dataset.desktopLayout.includes('thumbnail') && this.mql.matches) this.removeListSemantic();
      }

      onSlideChanged(event) {
        const thumbnail = this.elements.thumbnails.querySelector(
          `[data-target="${event.detail.currentElement.dataset.mediaId}"]`
        );
        this.setActiveThumbnail(thumbnail);
      }

      setActiveMedia(mediaId, prepend) {
        const activeMedia =
          this.elements.viewer.querySelector(`[data-media-id="${mediaId}"]`) ||
          this.elements.viewer.querySelector('[data-media-id]');
        if (!activeMedia) {
          return;
        }
        this.elements.viewer.querySelectorAll('[data-media-id]').forEach((element) => {
          element.classList.remove('is-active');
        });
        activeMedia?.classList?.add('is-active');

        if (prepend) {
          activeMedia.parentElement.firstChild !== activeMedia && activeMedia.parentElement.prepend(activeMedia);

          if (this.elements.thumbnails) {
            const activeThumbnail = this.elements.thumbnails.querySelector(`[data-target="${mediaId}"]`);
            activeThumbnail.parentElement.firstChild !== activeThumbnail && activeThumbnail.parentElement.prepend(activeThumbnail);
          }

          if (this.elements.viewer.slider) this.elements.viewer.resetPages();
        }

        this.preventStickyHeader();
        window.setTimeout(() => {
          if (!this.mql.matches || this.elements.thumbnails) {
            activeMedia.parentElement.scrollTo({ left: activeMedia.offsetLeft });
          }
          const activeMediaRect = activeMedia.getBoundingClientRect();
          // Don't scroll if the image is already in view
          if (activeMediaRect.top > -0.5) return;
          const top = activeMediaRect.top + window.scrollY;
          window.scrollTo({ top: top, behavior: 'smooth' });
        });
        this.playActiveMedia(activeMedia);

        if (!this.elements.thumbnails) return;
        const activeThumbnail = this.elements.thumbnails.querySelector(`[data-target="${mediaId}"]`);
        this.setActiveThumbnail(activeThumbnail);
        this.announceLiveRegion(activeMedia, activeThumbnail.dataset.mediaPosition);
      }

      setActiveThumbnail(thumbnail) {
        if (!this.elements.thumbnails || !thumbnail) return;

        this.elements.thumbnails
          .querySelectorAll('button')
          .forEach((element) => element.removeAttribute('aria-current'));
        thumbnail.querySelector('button').setAttribute('aria-current', true);
        if (this.elements.thumbnails.isSlideVisible(thumbnail, 10)) return;

        this.elements.thumbnails.slider.scrollTo({ left: thumbnail.offsetLeft });
      }

      announceLiveRegion(activeItem, position) {
        const image = activeItem.querySelector('.product__modal-opener--image img');
        if (!image) return;
        image.onload = () => {
          this.elements.liveRegion.setAttribute('aria-hidden', false);
          this.elements.liveRegion.innerHTML = window.accessibilityStrings.imageAvailable.replace('[index]', position);
          setTimeout(() => {
            this.elements.liveRegion.setAttribute('aria-hidden', true);
          }, 2000);
        };
        image.src = image.src;
      }

      playActiveMedia(activeItem) {
        window.pauseAllMedia();
        const deferredMedia = activeItem.querySelector('.deferred-media');
        if (deferredMedia) deferredMedia.loadContent(false);
      }

      preventStickyHeader() {
        this.stickyHeader = this.stickyHeader || document.querySelector('sticky-header');
        if (!this.stickyHeader) return;
        this.stickyHeader.dispatchEvent(new Event('preventHeaderReveal'));
      }

      removeListSemantic() {
        if (!this.elements.viewer.slider) return;
        this.elements.viewer.slider.setAttribute('role', 'presentation');
        this.elements.viewer.sliderItems.forEach((slide) => slide.setAttribute('role', 'presentation'));
      }
    }
  );
}

/* ---------------------------------------------------------------------------
   Evra London — keep the colour selector in step with the media gallery.

   Out of the box, picking a colour moves the gallery but not the other way
   round. This makes the link two-way: swipe the main image or click a
   thumbnail and the colour option below the sizes selects itself, which also
   updates the price, availability and the variant that gets added to the cart.

   Requires each colour's image to be assigned to that colour's variants in
   the product admin. Products without variant images are left alone.
   --------------------------------------------------------------------------- */
(function () {
  const COLOUR_OPTION = /colou?r|shade|finish/i;
  const SETTLE_MS = 4000;

  function ready(callback) {
    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', callback);
    } else {
      callback();
    }
  }

  function optionNames(product) {
    return (product.options || []).map((option) => (typeof option === 'string' ? option : option.name));
  }

  // Which option is the colour? Prefer the name, fall back to whichever option
  // reliably decides the variant image.
  function colourIndex(product) {
    const names = optionNames(product);
    const named = names.findIndex((name) => COLOUR_OPTION.test(name));
    if (named > -1) return named;

    for (let i = 0; i < names.length; i++) {
      const seen = new Map();
      let consistent = true;

      for (const variant of product.variants) {
        const media = variant.featured_media && variant.featured_media.id;
        if (!media) {
          consistent = false;
          break;
        }
        const value = variant.options[i];
        if (!seen.has(value)) seen.set(value, media);
        else if (seen.get(value) !== media) {
          consistent = false;
          break;
        }
      }

      if (consistent && seen.size > 1) return i;
    }

    return -1;
  }

  function buildMap(product) {
    const index = colourIndex(product);
    if (index < 0) return null;

    const byMedia = Object.create(null);
    product.variants.forEach((variant) => {
      const media = variant.featured_media && variant.featured_media.id;
      if (media) byMedia[String(media)] = variant.options[index];
    });

    if (!Object.keys(byMedia).length) return null;

    return {
      byMedia: byMedia,
      inputName: optionNames(product)[index] + '-' + (index + 1),
    };
  }

  function attach(gallery) {
    if (gallery.evraColourSync) return;

    const productInfo = gallery.closest('product-info') || document.querySelector('product-info');
    if (!productInfo || !productInfo.dataset.url) return;

    gallery.evraColourSync = true;

    const state = { map: null, internalUntil: 0 };

    fetch(productInfo.dataset.url + '.js')
      .then((response) => (response.ok ? response.json() : null))
      .then((product) => {
        if (product) state.map = buildMap(product);
      })
      .catch(() => {});

    const matches = (a, b) => String(a).trim().toLowerCase() === String(b).trim().toLowerCase();

    function selectColour(value) {
      const selects = productInfo.querySelector('variant-selects');
      if (!selects) return;

      const named = (element) => (element.name || '').trim() === state.map.inputName;

      let radio = null;
      selects.querySelectorAll('input[type="radio"]').forEach((element) => {
        if (named(element) && matches(element.value, value)) radio = element;
      });

      if (radio) {
        if (radio.checked || radio.disabled) return;
        radio.checked = true;
        commit(radio, value);
        return;
      }

      let dropdown = null;
      selects.querySelectorAll('select').forEach((element) => {
        if (named(element)) dropdown = element;
      });
      if (!dropdown) return;

      let option = null;
      Array.prototype.forEach.call(dropdown.options, (candidate) => {
        if (matches(candidate.value, value)) option = candidate;
      });
      if (!option || dropdown.value === option.value) return;

      dropdown.value = option.value;
      commit(dropdown, value);
    }

    function commit(element, value) {
      // Update the "Colour: Grey" label straight away so the swatch reads
      // correctly while the variant request is in flight.
      const fieldset = element.closest('fieldset');
      const label = fieldset && fieldset.querySelector('legend span');
      if (label) label.textContent = value;

      state.internalUntil = Date.now() + SETTLE_MS;
      element.dispatchEvent(new Event('change', { bubbles: true }));
    }

    function syncFromMedia(rawMediaId) {
      if (!state.map || !rawMediaId) return;
      // Viewer slides are prefixed with the section id; thumbnails are not.
      const mediaId = String(rawMediaId).split('-').pop();
      const value = state.map.byMedia[mediaId];
      if (value) selectColour(value);
    }

    const viewer = gallery.querySelector('[id^="GalleryViewer"]');
    if (viewer) {
      viewer.addEventListener('slideChanged', (event) => {
        const slide = event.detail && event.detail.currentElement;
        if (slide) syncFromMedia(slide.dataset.mediaId);
      });
    }

    // Thumbnail buttons get their handler bound at construction, so listen on
    // the way up rather than wrapping the method they captured.
    const thumbnails = gallery.querySelector('[id^="GalleryThumbnails"]');
    if (thumbnails) {
      thumbnails.addEventListener('click', (event) => {
        const item = event.target.closest('[data-target]');
        if (item) syncFromMedia(item.dataset.target);
      });
    }

    // Variant-driven gallery updates land here. When the gallery is moving
    // because we asked for a colour, skip the reorder so the strip doesn't
    // shuffle under the shopper's finger.
    const setActiveMedia = gallery.setActiveMedia;
    if (typeof setActiveMedia === 'function') {
      gallery.setActiveMedia = function (mediaId, prepend) {
        const internal = Date.now() < state.internalUntil;
        const result = setActiveMedia.call(this, mediaId, internal ? false : prepend);
        if (!internal) syncFromMedia(mediaId);
        return result;
      };
    }

    if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined' && PUB_SUB_EVENTS.variantChange) {
      subscribe(PUB_SUB_EVENTS.variantChange, () => {
        state.internalUntil = 0;
      });
    }
  }

  function scan() {
    document.querySelectorAll('media-gallery').forEach(attach);
  }

  ready(scan);
  document.addEventListener('product-info:loaded', scan);
})();
